import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider } from './lib/theme';
import { AuthProvider } from './lib/auth';
import { CapabilityProvider } from './lib/capabilities';
import { DiscoveryProvider } from './lib/discovery';
import { queryClient } from './lib/queryClient';
import { registerServiceWorker } from './pwa/registerServiceWorker';
import { applyBrandingOverlay, readCachedBrandingOverlay } from './lib/branding';
// `intro.js/introjs.css` (the retired `OnboardingTour`'s stylesheet) removed
// as of story 2-106 — see the SESSION-HANDOFF entry for the full sweep.
// Self-hosted fonts (story 2-61) - replaces the former external Google Fonts @import.
// Weights match the previous Google Fonts request exactly: 400/500/600/700/800, normal style.
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/outfit/800.css';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
import './styles/tokens.css';
import './index.css';

registerServiceWorker();

// Story 6-8, P10 §3's boot sequence: apply whatever branding overlay the LAST
// successful `/me/capabilities` fetch cached, synchronously, before React's
// first render — a returning user's second-and-later visits never paint the
// default `--primary` before the branded one. `CapabilityProvider` re-applies
// (and re-caches) from the live fetch once it resolves, which corrects a
// stale/cleared cache and keeps this in sync on every subsequent boot.
applyBrandingOverlay(readCachedBrandingOverlay());

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <CapabilityProvider>
              <DiscoveryProvider>
                <App />
              </DiscoveryProvider>
            </CapabilityProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);
